import { getCustomFieldsDefaultValues } from '@buildwise/libs/configurations';
import { ArticleSourceType, ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { ConfigEntryType } from 'config/config';
import * as Yup from 'yup';

export const getPredefinedFieldValidation = ({
  field,
  isCreatingOrEditingPlaylist,
}: {
  field: ConfigEntryType;
  isCreatingOrEditingPlaylist: boolean;
}) => {
  switch (field.entryCode) {
    case 'title': {
      return field?.referenceObject?.isRequired ||
        field?.referenceObject?.isRequired === undefined ||
        isCreatingOrEditingPlaylist
        ? Yup.string().required('Title is required')
        : Yup.string();
    }

    case 'description': {
      return field.referenceObject?.isRequired && !isCreatingOrEditingPlaylist
        ? Yup.string().required('Required field')
        : Yup.string();
    }

    case 'attachments':
    case 'playListArticles': {
      return field.referenceObject?.isRequired && !isCreatingOrEditingPlaylist
        ? Yup.array(Yup.object())
            .min(
              1,
              field.entryCode === 'attachments'
                ? 'Please upload at least one file'
                : 'Required field',
            )
            .required('Required field')
        : Yup.array(Yup.object());
    }

    case 'phoneNumber':
      return field.referenceObject?.isRequired && !isCreatingOrEditingPlaylist
        ? Yup.string()
            .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone number must be in the format (123) 456-7890')
            .required('Phone number is required')
        : Yup.string()
            .nullable()
            .notRequired()
            .test(
              'is-valid-phone',
              'Phone number must be in the format (123) 456-7890',
              (value) => !value || /^\(\d{3}\) \d{3}-\d{4}$/.test(value),
            );

    default: {
      return null;
    }
  }
};

export const getPredefinedFieldDefaultValue = ({
  article,
  defaultPlaylists,
  defaultProjectId,
  field,
}: {
  article?: ArticleType;
  defaultPlaylists?: { label: string; value: string }[];
  defaultProjectId?: string;
  field: ConfigEntryType;
}) => {
  switch (field.entryCode) {
    case 'title': {
      return article?.title || '';
    }

    case 'description': {
      return article?.description || '';
    }

    case 'divisions':
    case 'specSections':
    case 'partnerIds':
    case 'playListArticles':
    case 'attachments': {
      return (
        article?.[field.entryCode] ||
        (Array.isArray(article?.customFields?.[field.entryCode]?.fieldValue)
          ? article?.customFields?.[field.entryCode]?.fieldValue
          : [])
      );
    }

    case 'playListArticleIds': {
      return defaultPlaylists && defaultPlaylists.length > 0
        ? defaultPlaylists.map((item) => item.value)
        : article?.playListArticleIds || [];
    }

    case 'projectId': {
      return defaultProjectId || article?.projectId || '';
    }

    case 'referenceProjectIds':
      return defaultProjectId ? [defaultProjectId] : article?.referenceProjectIds ?? [];

    default: {
      return null;
    }
  }
};

export const getArticleFormDefaultValues = ({
  article,
  articleType,
  articleFields,
  defaultPlaylists,
  defaultProjectId,
}: {
  article?: ArticleType;
  articleType?: ArticleSourceType;
  articleFields: ConfigEntryType[];
  defaultPlaylists?: { label: string; value: string }[];
  defaultProjectId?: string;
}) => {
  const fieldsDefaultValues = getCustomFieldsDefaultValues({
    data: article,
    fields: articleFields,
    getPredefinedFieldDefaultValue: ({ field }) =>
      getPredefinedFieldDefaultValue({
        article,
        defaultPlaylists,
        defaultProjectId,
        field,
      }),
  });

  return {
    ...fieldsDefaultValues,
    // Check if divisions is enabled, then provide respective divisionChips default values
    ...(fieldsDefaultValues?.['divisions'] && {
      divisionChips: [],
    }),
    // Check if specSections is enabled, then provide respective specSectionChips default values
    ...(fieldsDefaultValues?.['specSections'] && {
      specSectionChips: [],
    }),
    isDraft: !!article?.isDraft,
    playlists: defaultPlaylists || [],
    isWritingWithAI: false,
    projectSpecific:
      article?.projectSpecific ||
      (articleType === ArticleSourceType.LESSON ? article?.projectSpecific === undefined : false),
    articleType: articleType || article?.articleType,
    relatedRecords: article?.relatedRecords || [],
    playListArticles: article?.playListArticles || [],
    playListArticleIds: article?.playListArticleIds || [],
  };
};
